/* Decline-with-template-email modal — ported from the prototype's openDecline().
   Template-driven (no free-text): pick a quick response from the firm's Diligentsia
   settings, preview/edit, then "Send & log". Sending persists the decline + queues
   the templated email via POST /deal/:id/decline and appends to the FCA decision log. */
import React, { useState } from 'react';

import { useProfile } from '../state';
import { useToast } from './Toast';
import ModalPortal from './ModalPortal';
import { declineDeal, ok } from '../api';

// Fallback quick responses if the profile hasn't loaded — mirrors Diligentsia defaults.
const FALLBACK_RESPONSES = [
	'Too early stage for us at the moment',
	'Has not demonstrated enough traction yet',
	'Not a good fit in terms of the type of funding you are seeking',
	'Operating in a sector in which we are not focused right now',
	'Not a good match in terms of the amount of funds you are seeking',
];

function firstNameFrom(deal) {
	const email = (deal && deal.founderContact) || (deal && deal.detail && deal.detail.contact);
	if (email && email.includes('@')) { const p = email.split('@')[0].split(/[._-]/)[0]; return p.charAt(0).toUpperCase() + p.slice(1); }
	return 'there';
}

function buildPreview(deal, reason, firmShort, signerName) {
	const first = firstNameFrom(deal);
	const lowered = reason ? reason.charAt(0).toLowerCase() + reason.slice(1) : '';
	const signer = signerName ? `${signerName} · ${firmShort}` : firmShort;
	return `Hi ${first},\n\nThank you for applying to ${firmShort} through Diligentsia. We won't be taking this forward: ${lowered}.\n\nThis isn't a reflection on ${deal.name} — through Diligentsia, your profile can be matched with investors whose criteria fit, and your investment-readiness workspace stays open to you either way.\n\nBest,\n${signer}`;
}

export default function DeclineModal({ deal, context, onClose, onDeclined }) {
	const showToast = useToast();
	const { profile } = useProfile();
	const firmShort = (profile && profile.short) || 'the fund';
	const signerName = profile && profile.preferredContact && profile.preferredContact.name;
	const quickResponses = (profile && profile.quickResponses && profile.quickResponses.length) ? profile.quickResponses : FALLBACK_RESPONSES;

	const suggested = deal.dealbreaker || quickResponses[0];
	const [reason, setReason] = useState(suggested);
	const [preview, setPreview] = useState(() => buildPreview(deal, suggested, firmShort, signerName));
	const [sending, setSending] = useState(false);

	const onReasonChange = e => {
		const next = e.target.value;
		setReason(next);
		setPreview(buildPreview(deal, next, firmShort, signerName));
	};

	const send = async () => {
		if (sending) return;
		setSending(true);
		const res = await declineDeal(deal.id, { reason, letter: preview, context });
		setSending(false);
		if (!ok(res)) return;                      // request helper already toasts the backend message
		onClose();
		showToast(`Decline sent to ${deal.name} · decision logged (reason · who · when)`);
		setTimeout(() => showToast(`${deal.name} profile released for matching with other VCs on Diligentsia`, true), 900);
		if (onDeclined) onDeclined();
	};

	return (
		<ModalPortal>
		<div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
			<div className="modal" role="dialog" aria-label="Decline with template email">
				<div className="modal__title">Decline {deal.name}</div>
				<div className="modal__desc">Pick a quick response from your Diligentsia settings — the email is editable before it sends. No free-text box: templates keep the tone consistent.</div>
				<select value={reason} onChange={onReasonChange}>
					{quickResponses.map(q => <option key={q} value={q}>{q}</option>)}
				</select>
				<textarea value={preview} onChange={e => setPreview(e.target.value)} />
				<div className="modal__log">Decision log entry · <strong>{context === 'triage' ? 'Quick reject at triage' : 'Quick reject at gate'}</strong> — reason: <strong>{reason}</strong> · by <strong>{signerName ? `${signerName}, ${firmShort}` : firmShort}</strong> · <strong>today</strong></div>
				<div className="modal__actions">
					<button className="pill pill--ghost" onClick={onClose} disabled={sending}>Cancel</button>
					<button className="pill pill--primary" onClick={send} disabled={sending}>{sending ? 'Sending…' : 'Send & log decision'}</button>
				</div>
			</div>
		</div>
		</ModalPortal>
	);
}
